import React, { useEffect } from 'react';
import {
  ArrowUpRight,
  CheckCircle2,
  Mail,
  MessageCircle,
  ShieldCheck,
} from 'lucide-react';

const decodeEmailToken = (token) => {
  if (!token) return '';

  try {
    const normalized = token.replace(/-/g, '+').replace(/_/g, '/');
    return window.atob(normalized);
  } catch (error) {
    console.error('Kontaktadresse konnte nicht geöffnet werden:', error);
    return '';
  }
};

const PrelaunchPage = ({ settings, onDeveloperAccess }) => {
  const {
    headline,
    message,
    website_url: websiteUrl,
    email_token: emailToken,
    has_email: hasEmail,
    whatsapp_number: whatsappNumber,
    whatsapp_message: whatsappMessage,
  } = settings;

  useEffect(() => {
    const globalHeader = document.querySelector('body > header');
    const globalFooter = document.querySelector('body > footer');
    const previousHeaderDisplay = globalHeader?.style.display || '';
    const previousFooterDisplay = globalFooter?.style.display || '';

    if (globalHeader) globalHeader.style.display = 'none';
    if (globalFooter) globalFooter.style.display = 'none';
    document.body.style.backgroundColor = '#071A2B';

    return () => {
      if (globalHeader) globalHeader.style.display = previousHeaderDisplay;
      if (globalFooter) globalFooter.style.display = previousFooterDisplay;
      document.body.style.backgroundColor = '';
    };
  }, []);

  const openEmail = () => {
    const email = decodeEmailToken(emailToken);
    if (!email) return;

    const subject = encodeURIComponent('Interesse an FW Kantine');
    const body = encodeURIComponent(
      'Hallo,\n\nwir interessieren uns für FW Kantine und möchten mehr erfahren.\n\nViele Grüße'
    );
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const whatsappUrl = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage || '')}`
    : null;

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#071A2B] text-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(circle at 12% 20%, rgba(238,108,77,0.24), transparent 29%), radial-gradient(circle at 88% 12%, rgba(57,189,190,0.19), transparent 30%), linear-gradient(145deg, #071A2B 0%, #0B263A 52%, #102D3D 100%)',
        }}
      />
      <div
        className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full border border-white/10 sm:h-96 sm:w-96"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-4 top-44 h-52 w-52 rounded-full border border-[#39BDBE]/20 sm:h-72 sm:w-72"
        aria-hidden="true"
      />

      <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col px-5 py-6 sm:px-8 sm:py-8 lg:px-12">
        <div role="banner" className="flex items-center justify-between">
          <a
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 rounded-full focus:outline-none focus:ring-2 focus:ring-[#EE6C4D] focus:ring-offset-4 focus:ring-offset-[#071A2B]"
          >
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#EE6C4D] text-sm font-black tracking-tight shadow-lg shadow-black/20">
              FW
            </span>
            <span className="text-sm font-semibold tracking-[0.16em] text-white/90 sm:text-base">
              KANTINE
            </span>
          </a>

          <span className="hidden rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white/70 backdrop-blur sm:inline-flex">
            Für Feuerwachen entwickelt
          </span>
        </div>

        <section className="grid flex-1 items-center gap-12 py-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(300px,0.75fr)] lg:py-16">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#39BDBE]/30 bg-[#39BDBE]/10 px-4 py-2 text-sm font-medium text-[#8BE1DF]">
              <span className="h-2 w-2 rounded-full bg-[#39BDBE]" />
              Diese Wache ist vorbereitet
            </div>

            <h1 className="max-w-3xl text-4xl font-bold leading-[1.08] tracking-[-0.035em] text-white sm:text-5xl md:text-6xl lg:text-7xl">
              {headline}
            </h1>
            <p className="mt-6 max-w-2xl whitespace-pre-line text-lg leading-8 text-[#C8D5DD] sm:text-xl">
              {message}
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#EE6C4D] px-6 py-3 font-semibold text-white shadow-lg shadow-black/20 transition hover:bg-[#F27E62] focus:outline-none focus:ring-2 focus:ring-[#EE6C4D] focus:ring-offset-4 focus:ring-offset-[#071A2B]"
              >
                Alle Informationen
                <ArrowUpRight size={19} aria-hidden="true" />
              </a>

              {hasEmail && (
                <button
                  type="button"
                  onClick={openEmail}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3 font-semibold text-white backdrop-blur transition hover:border-white/35 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-4 focus:ring-offset-[#071A2B]"
                >
                  <Mail size={19} aria-hidden="true" />
                  E-Mail schreiben
                </button>
              )}

              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-[#52C68A]/35 bg-[#52C68A]/10 px-6 py-3 font-semibold text-[#B8F1D2] transition hover:border-[#52C68A]/55 hover:bg-[#52C68A]/15 focus:outline-none focus:ring-2 focus:ring-[#52C68A] focus:ring-offset-4 focus:ring-offset-[#071A2B]"
                >
                  <MessageCircle size={19} aria-hidden="true" />
                  WhatsApp
                </a>
              )}
            </div>
          </div>

          <aside className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-[#39BDBE]/20 to-[#EE6C4D]/20 blur-xl" />
            <div className="relative rounded-[1.75rem] border border-white/15 bg-white/[0.07] p-6 shadow-2xl shadow-black/25 backdrop-blur-xl sm:p-8">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8BE1DF]">
                Bereit für euren Alltag
              </p>
              <h2 className="mt-3 text-2xl font-semibold leading-tight">
                Eine zentrale Lösung für die Kantinenorganisation.
              </h2>

              <div className="mt-7 space-y-5">
                {[
                  'Bestellungen übersichtlich verwalten',
                  'Abrechnungen und Salden nachvollziehen',
                  'Für Smartphone, Tablet und Desktop',
                ].map((benefit) => (
                  <div key={benefit} className="flex gap-3">
                    <CheckCircle2
                      className="mt-0.5 shrink-0 text-[#39BDBE]"
                      size={21}
                      aria-hidden="true"
                    />
                    <span className="leading-6 text-[#DCE6EB]">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 border-t border-white/10 pt-6">
                <div className="flex items-center gap-3 text-sm text-white/65">
                  <ShieldCheck size={20} className="text-[#EEA38F]" aria-hidden="true" />
                  <span>Persönlich eingerichtet und begleitet</span>
                </div>
              </div>
            </div>
          </aside>
        </section>

        <div role="contentinfo" className="flex flex-col gap-4 border-t border-white/10 py-5 text-xs text-white/45 sm:flex-row sm:items-center sm:justify-between">
          <span>FW Kantine · Digitale Kantinenverwaltung für Feuerwachen</span>
          {onDeveloperAccess && (
            <button
              type="button"
              onClick={onDeveloperAccess}
              className="self-start rounded-md px-1 py-1 text-white/40 transition hover:text-white/75 focus:outline-none focus:ring-2 focus:ring-white/40 sm:self-auto"
            >
              Developer-Bereich
            </button>
          )}
        </div>
      </div>
    </main>
  );
};

export default PrelaunchPage;
